// Phase COTTON-RM-002 Part B — Mobile-first moisture entry.
//
// Stand-alone page for moisture testers. The deep entry form already
// lives inside RMQualityPage's GRN drawer; this page surfaces every
// pending moisture plan as a card so the operator goes straight to
// the right GRN without hunting through menus.
//
// Save path uses POST /grn/{id}/bulk-moisture — fixed in
// Phase COTTON-QC-001.

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, App, Badge, Button, Card, Col, Empty, Grid, Input, InputNumber, Modal,
  Progress, Row, Segmented, Select, Skeleton, Space, Statistic, Tabs,
  Tag, Typography,
} from 'antd'
import {
  ArrowLeftOutlined, ExclamationCircleOutlined, PlayCircleOutlined,
  ReloadOutlined, ScanOutlined, SaveOutlined, CheckCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const { useBreakpoint } = Grid

import apiClient from '@/shared/api/client'
import { cottonRmApi } from '@/features/cotton-rm/cottonRmApi'
import { usePlantId } from '@/shared/hooks/usePlantId'
import { QRScanButton } from '@/shared/components/QRScanModal'

const { Text, Title } = Typography

// Phase COTTON-MOISTURE-POLISH — page-scoped CSS injected once via a static
// <style> element. Kept inline so this file remains self-contained, and the
// rules are scoped to .moisture-page-root so they can't leak into other pages.
// All animations respect prefers-reduced-motion via @media query.
const MOISTURE_CSS = `
.moisture-page-root .bale-card {
  border-radius: 12px;
  border: 1.5px solid #e6f4f1;
  background: #fff;
  transition: transform 180ms ease-out,
              border-color 180ms ease-out,
              box-shadow 180ms ease-out,
              background 180ms ease-out;
  will-change: transform;
}
.moisture-page-root .bale-card:hover {
  border-color: #87e8de;
  box-shadow: 0 2px 8px rgba(19, 194, 194, 0.12);
}
.moisture-page-root .bale-card--focused {
  transform: scale(1.015);
  border-color: #13c2c2;
  box-shadow: 0 4px 16px rgba(19, 194, 194, 0.20);
}
.moisture-page-root .bale-card--filled {
  border-color: #52c41a;
  background: linear-gradient(180deg, #f6ffed 0%, #fff 60%);
}
.moisture-page-root .bale-card--filled .bale-check {
  opacity: 1;
  transform: scale(1) rotate(0);
}
.moisture-page-root .bale-card--hold {
  border-color: #ff7875;
  background: linear-gradient(180deg, #fff1f0 0%, #fff 60%);
}
.moisture-page-root .bale-check {
  opacity: 0;
  transform: scale(0.5) rotate(-30deg);
  transition: opacity 200ms ease-out, transform 250ms cubic-bezier(0.34, 1.56, 0.64, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px; height: 22px;
  border-radius: 50%;
  background: #52c41a;
  color: #fff;
  font-size: 13px;
}

.moisture-page-root .moisture-input-wrap {
  position: relative;
}
.moisture-page-root .moisture-input-wrap .ant-input-number {
  width: 100%;
}
/* Mobile-friendly large numeric input */
.moisture-page-root .moisture-input-wrap .ant-input-number-input {
  font-size: 22px !important;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.5px;
  height: 48px !important;
  line-height: 48px !important;
  font-variant-numeric: tabular-nums;
}

/* Sticky bottom action bar — only on mobile/tablet */
.moisture-page-root .sticky-actions {
  position: sticky;
  bottom: 0;
  left: 0; right: 0;
  z-index: 30;
  margin: 16px -16px -16px -16px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid #e6f4f1;
  box-shadow: 0 -2px 12px rgba(19, 194, 194, 0.08);
  display: flex;
  gap: 8px;
}
.moisture-page-root .sticky-actions .ant-btn {
  flex: 1;
  height: 48px;
  font-size: 15px;
  border-radius: 10px;
  font-weight: 600;
}
@media (min-width: 992px) {
  .moisture-page-root .sticky-actions { display: none; }
}

/* Compact comparison strip — already responsive via Col, just polish */
.moisture-page-root .comparison-tile {
  border-radius: 10px;
  padding: 10px 12px;
  text-align: center;
  transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1);
}
.moisture-page-root .comparison-tile--warehouse-pass {
  background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
  border: 1px solid #b7eb8f;
}
.moisture-page-root .comparison-tile--warehouse-fail {
  background: linear-gradient(135deg, #fff2f0 0%, #ffccc7 100%);
  border: 1px solid #ffa39e;
  animation: shake-once 320ms cubic-bezier(0.36, 0.07, 0.19, 0.97);
}
.moisture-page-root .comparison-tile--warehouse-empty {
  background: #fff;
  border: 1px solid #87e8de;
}

@keyframes shake-once {
  10%, 90% { transform: translateX(-1px); }
  20%, 80% { transform: translateX(2px); }
  30%, 50%, 70% { transform: translateX(-3px); }
  40%, 60% { transform: translateX(3px); }
}

/* GRN list cards — bigger, hoverable, tablet-friendly */
.moisture-page-root .grn-card {
  border-radius: 14px;
  transition: transform 180ms ease-out, box-shadow 180ms ease-out, border-color 180ms ease-out;
  cursor: pointer;
}
.moisture-page-root .grn-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
}
.moisture-page-root .grn-card:active {
  transform: translateY(0);
}

/* Save button success pulse */
.moisture-page-root .save-success {
  animation: pulse-green 600ms ease-out;
}
@keyframes pulse-green {
  0%   { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.6); }
  70%  { box-shadow: 0 0 0 14px rgba(82, 196, 26, 0); }
  100% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0); }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .moisture-page-root .bale-card,
  .moisture-page-root .grn-card,
  .moisture-page-root .comparison-tile,
  .moisture-page-root .bale-check {
    transition: none !important;
    animation: none !important;
    transform: none !important;
  }
}
`

function useMoistureStyles() {
  useEffect(() => {
    const id = 'moisture-page-css'
    if (document.getElementById(id)) return
    const style = document.createElement('style')
    style.id = id
    style.textContent = MOISTURE_CSS
    document.head.appendChild(style)
  }, [])
}

interface Bale {
  id: string
  bale_no: string
  weight_kg: number
  moisture_pct: number | null
  is_moisture_sample: boolean
  qc_status: string
  // Phase COTTON-MOISTURE-CONSOLIDATE — lot identification
  party_lot_no?: string | null
  mill_lot_no?: string | null
  lot_no?: string | null
}
interface GRN {
  id: string
  grn_no: string
  supplier_name?: string | null
  physical_bale_count: number
  sampling_pct: number | null
  moisture_sampling_pct: number | null
  avg_moisture_pct: number | null
  bales: Bale[]
  do_no?: string | null
  po_no?: string | null
}

// Phase COTTON-MOISTURE-CONSOLIDATE — deduplicate non-null lot identifiers
// from a GRN's bales array. Caller decides how to render (badges, count, etc.).
function uniqueLots(bales: Bale[], field: 'party_lot_no' | 'mill_lot_no'): string[] {
  const seen = new Set<string>()
  for (const b of bales) {
    const v = b[field]
    if (v && !seen.has(v)) seen.add(v)
  }
  return [...seen]
}

// Compact lot tag list — shows up to 3 then "+N more" badge
function LotTags({ lots, color, label }: {
  lots: string[]; color: string; label: string
}) {
  if (lots.length === 0) {
    return <Text type="secondary" style={{ fontSize: 11 }}>{label}: —</Text>
  }
  const head = lots.slice(0, 3)
  const rest = lots.length - head.length
  return (
    <Space size={2} wrap>
      <Text type="secondary" style={{ fontSize: 11 }}>{label}:</Text>
      {head.map(l => <Tag key={l} color={color} style={{ marginInlineEnd: 0 }}>{l}</Tag>)}
      {rest > 0 && <Tag style={{ marginInlineEnd: 0 }}>+{rest} more</Tag>}
    </Space>
  )
}

function classify(g: GRN): 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'HOLD' {
  const sampled = g.bales.filter(b => b.is_moisture_sample)
  if (sampled.length === 0) return 'PENDING'
  const done = sampled.filter(b => b.moisture_pct != null).length
  const anyHold = g.bales.some(b => b.qc_status === 'HOLD')
  if (anyHold) return 'HOLD'
  if (done === 0) return 'PENDING'
  if (done < sampled.length) return 'IN_PROGRESS'
  return 'COMPLETED'
}


// Phase COTTON-MOISTURE-CONSOLIDATE — types for lab-comparison endpoint
interface LabCompareParam {
  name: string
  spec_max?: number | string | null
  spec_min?: number | string | null
  parakh_avg?: number | string | null
  received_avg?: number | string | null
}
interface LabComparison {
  parameters: LabCompareParam[]
  moisture_sampling_pct?: number | null
}

function GrnEntry({ grn: grnFromList, onBack, onSaved }: {
  grn: GRN
  onBack: () => void
  onSaved: () => void
}) {
  const { message } = App.useApp()
  const qcClient = useQueryClient()
  const screens = useBreakpoint()
  const isMobile = !screens.md  // < 768px
  // Refs for auto-advance focus across the bale card grid
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const [focusedBaleId, setFocusedBaleId] = useState<string | null>(null)
  const [recentlySaved, setRecentlySaved] = useState(false)

  // Phase COTTON-MOISTURE-CONSOLIDATE — self-fetch the GRN so mutations
  // (sampling plan, save readings, add bale) reflect immediately without
  // waiting for the list query to refetch. Same pattern as RMQualityPage's
  // grn-detail query — fixes "Set Moisture Plan succeeded but no bales
  // appear below" stale-reference bug.
  const { data: grnFresh } = useQuery({
    queryKey: ['grn-detail-moisture', grnFromList.id],
    queryFn: () => apiClient.get<GRN>(`/grn/${grnFromList.id}`).then(r => r.data),
    initialData: grnFromList,
  })
  const grn = grnFresh ?? grnFromList

  const sampled = grn.bales.filter(b => b.is_moisture_sample)
  const others = grn.bales.filter(b => !b.is_moisture_sample)
  const [map, setMap] = useState<Record<string, number | null>>(() => {
    const m: Record<string, number | null> = {}
    for (const b of sampled) m[b.id] = b.moisture_pct
    return m
  })
  const [extra, setExtra] = useState<string>('')  // manual bale code add
  const [addExtraOpen, setAddExtraOpen] = useState(false)
  // Phase COTTON-MOISTURE-CONSOLIDATE — sampling-plan picker state
  const [planPct, setPlanPct] = useState<number>(
    grn.moisture_sampling_pct ?? 25,
  )

  // Pull 3-way lab comparison so we can show PO Spec / Parakh Avg / Warehouse Avg
  const { data: labCompare } = useQuery({
    queryKey: ['grn-lab-compare', grn.id],
    queryFn: () => apiClient.get<LabComparison>(`/grn/${grn.id}/lab-comparison`)
      .then(r => r.data),
  })
  const moistureRow = labCompare?.parameters?.find(p => p.name === 'Moisture (%)')
  const specMax = moistureRow?.spec_max != null
    ? Number(moistureRow.spec_max) : null
  const parakhAvg = moistureRow?.parakh_avg != null
    ? Number(moistureRow.parakh_avg) : null
  // Warehouse avg: live computation from entered readings (operator-friendly)

  const completionPct = useMemo(() => {
    if (sampled.length === 0) return 0
    const filled = sampled.filter(b => map[b.id] != null).length
    return Math.round((filled / sampled.length) * 100)
  }, [map, sampled])

  const moistures = useMemo(
    () => Object.values(map).filter((v): v is number => v != null),
    [map],
  )
  const avg = moistures.length
    ? moistures.reduce((a, b) => a + b, 0) / moistures.length : null
  const warehousePass = avg != null && specMax != null ? avg <= specMax : null
  const minV = moistures.length ? Math.min(...moistures) : null
  const maxV = moistures.length ? Math.max(...moistures) : null

  // Party / Mill lot rollups for the header
  const partyLots = uniqueLots(grn.bales, 'party_lot_no')
  const millLots = uniqueLots(grn.bales, 'mill_lot_no')

  // Resolve a scanned/typed value to a bale in this GRN. Accepts:
  //   - plain bale_no (e.g. "BL-2026-02882")
  //   - UCL QR format "UCL:BALE:<plant>:<bale_no>"
  //   - the bale's full qr_code_value (matched against bales array)
  // Returns the matching bale or null.
  function resolveBale(raw: string) {
    const trimmed = (raw || '').trim()
    if (!trimmed) return null
    // UCL QR: take everything after the last ':'
    const candidate = trimmed.includes(':')
      ? trimmed.split(':').pop()!.trim()
      : trimmed
    const upper = candidate.toUpperCase()
    // Match by bale_no (most common)
    const byBaleNo = grn.bales.find(
      b => b.bale_no?.toUpperCase() === upper,
    )
    return byBaleNo ?? null
  }

  function tryAddByCode(raw: string) {
    const found = resolveBale(raw)
    if (!found) {
      message.error(`Bale "${raw}" not found in this GRN`)
      return
    }
    if (found.is_moisture_sample) {
      message.warning(`${found.bale_no} is already in the moisture sample`)
      return
    }
    addExtraMut.mutate(found.id)
  }

  const addExtraMut = useMutation({
    mutationFn: (baleId: string) =>
      apiClient.post<GRN>(`/grn/${grn.id}/sample-bales/${baleId}`, undefined,
        { params: { sample_type: 'MOISTURE' } }).then(r => r.data),
    onSuccess: (updatedGrn) => {
      message.success('Bale added as extra moisture sample.')
      setAddExtraOpen(false); setExtra('')
      qcClient.setQueryData(['grn-detail-moisture', grn.id], updatedGrn)
      onSaved()
    },
    onError: (e: Error) => message.error(e.message),
  })

  // Phase COTTON-MOISTURE-CONSOLIDATE — set moisture sampling plan
  const samplingMut = useMutation({
    // Backend schema field is `sampling_pct`, not `pct` (fixed after 422 on prod)
    mutationFn: () =>
      apiClient.post<GRN>(`/grn/${grn.id}/sampling`, {
        sampling_pct: planPct, sample_type: 'MOISTURE',
      }).then(r => r.data),
    onSuccess: (updatedGrn) => {
      message.success(`Moisture sampling plan set at ${planPct}%.`)
      // Update the detail cache with the server's authoritative response so
      // the per-bale cards render immediately. Without this, the page would
      // wait for the list query to refetch before showing newly-sampled bales.
      qcClient.setQueryData(['grn-detail-moisture', grn.id], updatedGrn)
      qcClient.invalidateQueries({ queryKey: ['grn-lab-compare', grn.id] })
      onSaved()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(
      e?.response?.data?.detail ?? e?.message ?? 'Failed to set plan',
    ),
  })

  const saveMut = useMutation({
    mutationFn: async () => {
      const rows = sampled
        .filter(b => map[b.id] != null)
        .map(b => ({ bale_id: b.id, moisture_pct: map[b.id]! }))
      if (rows.length === 0) {
        throw new Error('No moisture readings to save')
      }
      const r = await apiClient.post<GRN>(
        `/grn/${grn.id}/bulk-moisture`, { rows })
      return r.data
    },
    onSuccess: (updatedGrn) => {
      message.success('Moisture saved.')
      qcClient.setQueryData(['grn-detail-moisture', grn.id], updatedGrn)
      qcClient.invalidateQueries({ queryKey: ['grn-lab-compare', grn.id] })
      onSaved()
      setRecentlySaved(true)
      setTimeout(() => setRecentlySaved(false), 800)
    },
    onError: (e: Error) => message.error(e.message),
  })

  return (
    <div>
      {/* Sticky compact header on mobile so back + GRN no stay visible
          while operator scrolls through bale cards. Plain Space on desktop. */}
      <div style={isMobile ? {
        position: 'sticky', top: 0, zIndex: 20,
        margin: '-16px -16px 12px',
        padding: '12px 16px',
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid #f0f0f0',
      } : { marginBottom: 12 }}>
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}
                  size={isMobile ? 'large' : 'middle'}
                  style={isMobile ? { height: 40, borderRadius: 8 } : undefined}>
            {isMobile ? '' : 'Back'}
          </Button>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>{grn.grn_no}</Title>
          {grn.do_no && <Tag color="cyan">DO {grn.do_no}</Tag>}
          {grn.po_no && <Tag color="geekblue">PO {grn.po_no}</Tag>}
        </Space>
      </div>

      {/* Identification row — Party Lot + Mill Lot + supplier */}
      <Card style={{ marginBottom: 12 }} size="small">
        <Row gutter={[12, 8]}>
          <Col xs={24} md={8}>
            <Text type="secondary" style={{ fontSize: 11 }}>Supplier</Text>
            <div><Text strong>{grn.supplier_name ?? '—'}</Text></div>
          </Col>
          <Col xs={24} md={8}>
            <LotTags lots={partyLots} color="purple" label="Party Lot" />
          </Col>
          <Col xs={24} md={8}>
            <LotTags lots={millLots} color="blue" label="Mill Lot" />
          </Col>
        </Row>
      </Card>

      {/* ── Warehouse Moisture Check — moved from RMQualityPage ──
          Phase COTTON-MOISTURE-CONSOLIDATE: all moisture entry now lives
          on this single page. The 3-column strip compares PO contract,
          Parakh pre-shipment, and live warehouse readings. */}
      <Card
        size="small"
        style={{ border: '1px solid #87e8de', background: '#e6fffb',
                  marginBottom: 12 }}
        styles={{ body: { padding: '12px 16px' } }}
        title={
          <Row align="middle" justify="space-between" wrap gutter={[8, 4]}>
            <Col>
              <Space size={6}>
                <span style={{ fontSize: 16 }}>🌡️</span>
                <Text strong style={{ fontSize: 13 }}>Warehouse Moisture Check</Text>
                <Tag color="cyan" style={{ fontSize: 10 }}>
                  {sampled.length > 0
                    ? `${grn.moisture_sampling_pct ?? planPct}% Sample`
                    : 'No plan set'}
                </Tag>
              </Space>
            </Col>
            <Col>
              <Space size={8} wrap>
                <Segmented
                  size="small"
                  value={planPct}
                  onChange={(v) => setPlanPct(v as number)}
                  options={[
                    { label: '25%', value: 25 },
                    { label: '50%', value: 50 },
                    { label: '75%', value: 75 },
                    { label: '100%', value: 100 },
                  ]}
                />
                <Button size="small"
                  loading={samplingMut.isPending}
                  onClick={() => samplingMut.mutate()}>
                  Set Moisture Plan
                </Button>
                <Button type="primary" size="small" icon={<SaveOutlined />}
                  loading={saveMut.isPending}
                  disabled={moistures.length === 0}
                  onClick={() => saveMut.mutate()}
                  style={{ background: '#13c2c2', borderColor: '#13c2c2' }}>
                  Save ({moistures.length}/{sampled.length})
                </Button>
              </Space>
            </Col>
          </Row>
        }
      >
        {/* 3-column comparison strip — class-driven so passes/fails animate */}
        <Row gutter={8} style={{ marginBottom: 12 }}>
          <Col xs={8}>
            <div className="comparison-tile"
                 style={{ background: '#e6f4ff', border: '1px solid #91caff' }}>
              <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>PO Spec (Max)</Text>
              <Text strong style={{ fontSize: 22, color: '#1677ff', fontFamily: 'monospace' }}>
                {specMax != null ? `${specMax}%` : '—'}
              </Text>
              <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Contract Limit</Text>
            </div>
          </Col>
          <Col xs={8}>
            <div className="comparison-tile"
                 style={{ background: '#f9f0ff', border: '1px solid #d3adf7' }}>
              <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Parakh Avg</Text>
              <Text strong style={{ fontSize: 22, color: '#722ed1', fontFamily: 'monospace' }}>
                {parakhAvg != null ? `${parakhAvg}%` : '—'}
              </Text>
              <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Pre-Shipment</Text>
            </div>
          </Col>
          <Col xs={8}>
            <div
              className={
                'comparison-tile ' + (
                  warehousePass === true  ? 'comparison-tile--warehouse-pass'
                : warehousePass === false ? 'comparison-tile--warehouse-fail'
                                          : 'comparison-tile--warehouse-empty')
              }
            >
              <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Warehouse Avg</Text>
              <Text strong style={{
                fontSize: 22,
                color: warehousePass === false ? '#ff4d4f'
                       : warehousePass === true ? '#52c41a' : '#13c2c2',
                fontFamily: 'monospace',
              }}>
                {avg != null ? `${avg.toFixed(2)}%` : '—'}
              </Text>
              <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                {avg == null ? 'Enter readings below'
                 : warehousePass === true ? '✓ Within spec'
                 : warehousePass === false ? '✗ Exceeds spec'
                 : ''}
              </Text>
            </div>
          </Col>
        </Row>

        {/* Min/Max strip when readings exist */}
        {moistures.length > 0 && (
          <Row gutter={8} style={{ marginBottom: 4 }}>
            <Col xs={8}><Statistic title="Min" value={minV ?? 0}
              suffix="%" precision={2} valueStyle={{ fontSize: 14 }} /></Col>
            <Col xs={8}><Statistic title="Max" value={maxV ?? 0}
              suffix="%" precision={2} valueStyle={{ fontSize: 14 }} /></Col>
            <Col xs={8}><Statistic title="Bales tested"
              value={moistures.length} suffix={` / ${sampled.length}`}
              valueStyle={{ fontSize: 14 }} /></Col>
          </Row>
        )}
        <Progress percent={completionPct} size="small"
          status={completionPct === 100 ? 'success' : 'active'} />

        {/* When no sampling plan is set yet, show prominent CTA */}
        {sampled.length === 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 10 }}
            message="No moisture sample plan set yet"
            description={
              <>Pick a sample size above (default 25%) and click <b>Set Moisture Plan</b>.
                The system will mark N bales as moisture samples, then you can
                enter per-bale readings below.</>
            }
          />
        )}

        {/* Add Extra Bale (only visible once plan is set) */}
        {sampled.length > 0 && (
          <Space wrap style={{ marginTop: 10 }}>
            <Button size="small" icon={<ScanOutlined />}
              onClick={() => setAddExtraOpen(true)}>
              Add Extra Bale to Sample
            </Button>
          </Space>
        )}
      </Card>

      <Row gutter={[12, 12]}>
        {sampled.map((b, idx) => {
          const filled = map[b.id] != null
          const focused = focusedBaleId === b.id
          const hold = b.qc_status === 'HOLD'
          const cls = [
            'bale-card',
            filled  && 'bale-card--filled',
            focused && 'bale-card--focused',
            hold    && 'bale-card--hold',
          ].filter(Boolean).join(' ')
          return (
            <Col xs={24} sm={12} md={8} lg={6} key={b.id}>
              <Card size="small" bordered={false} className={cls}>
                <Space direction="vertical" style={{ width: '100%' }} size={6}>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text strong style={{ fontSize: 16, letterSpacing: 0.3 }}>
                      {b.bale_no}
                    </Text>
                    <span className="bale-check" aria-hidden>
                      <CheckCircleOutlined />
                    </span>
                  </Space>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {b.party_lot_no && <>Party {b.party_lot_no} · </>}
                    {b.mill_lot_no && <>Mill {b.mill_lot_no} · </>}
                    {b.weight_kg} kg · QC {b.qc_status}
                  </Text>
                  <div className="moisture-input-wrap">
                    <InputNumber
                      value={map[b.id] ?? undefined}
                      onChange={(v) => setMap(m => ({
                        ...m, [b.id]: v == null ? null : Number(v),
                      }))}
                      onFocus={() => setFocusedBaleId(b.id)}
                      onBlur={() => setFocusedBaleId(prev => prev === b.id ? null : prev)}
                      min={0} max={20} step={0.1}
                      placeholder="Moisture %"
                      addonAfter="%"
                      // Mobile: bring up the decimal keypad, not the full alpha keyboard
                      inputMode="decimal"
                      controls={false}
                      ref={(el) => {
                        // AntD's InputNumber exposes .input internally; we
                        // grab the underlying <input> for auto-advance focus
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const node = (el as any)?.input as HTMLInputElement | undefined
                        inputRefs.current[b.id] = node ?? null
                      }}
                      onPressEnter={() => {
                        // Auto-advance: jump to next bale's input on Enter.
                        // Floor operators do 25+ bales — this saves a tap each.
                        const next = sampled[idx + 1]
                        if (next) inputRefs.current[next.id]?.focus()
                      }}
                    />
                  </div>
                  {hold && (
                    <Tag color="error" icon={<ExclamationCircleOutlined />}>
                      QC HOLD
                    </Tag>
                  )}
                </Space>
              </Card>
            </Col>
          )
        })}
      </Row>

      {sampled.length > 0 && others.length > 0 && (
        <Card title={`Non-sampled bales (${others.length})`}
              style={{ marginTop: 16 }} size="small">
          <Text type="secondary">
            These bales are not in the moisture sample list. Use "Add Extra Bale" above to include any.
          </Text>
        </Card>
      )}

      {/* Phase COTTON-MOISTURE-POLISH — sticky bottom action bar on
          mobile/tablet so Save + Add are reachable in the thumb zone
          without scrolling. Hidden on lg+ via CSS media query. */}
      {sampled.length > 0 && (
        <div className="sticky-actions">
          <Button size="large" icon={<PlusOutlined />}
                  onClick={() => setAddExtraOpen(true)}>
            Add
          </Button>
          <Button size="large" icon={<ScanOutlined />}
                  onClick={() => setAddExtraOpen(true)}>
            Scan
          </Button>
          <Button type="primary" size="large" icon={<SaveOutlined />}
                  loading={saveMut.isPending}
                  disabled={moistures.length === 0}
                  className={recentlySaved ? 'save-success' : undefined}
                  onClick={() => saveMut.mutate()}
                  style={{
                    background: '#13c2c2', borderColor: '#13c2c2',
                    flex: 2,
                  }}>
            Save ({moistures.length}/{sampled.length})
          </Button>
        </div>
      )}

      <Modal open={addExtraOpen}
              title="Add Bale to Moisture Sample"
              onCancel={() => setAddExtraOpen(false)}
              onOk={() => tryAddByCode(extra)}
              okText="Add"
              okButtonProps={{ size: 'large', style: { height: 44, borderRadius: 8, fontWeight: 600 } }}
              cancelButtonProps={{ size: 'large', style: { height: 44, borderRadius: 8 } }}
              width={isMobile ? '100%' : 480}
              style={isMobile ? { top: 0, maxWidth: '100%', margin: 0, padding: 0 } : undefined}
              styles={isMobile ? { body: { padding: 20 } } : undefined}
              confirmLoading={addExtraMut.isPending}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {/* Scan button is the primary affordance on mobile — big & first */}
          <QRScanButton
            primary
            size="large"
            label="📷  Scan Bale QR"
            modalTitle="Scan Bale QR"
            style={{ width: '100%', height: 56, fontSize: 16,
                      borderRadius: 12, fontWeight: 600 }}
            onScan={(scanned) => {
              setExtra(scanned)
              tryAddByCode(scanned)
            }}
          />
          <Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: 12 }}>
            …or enter the bale number manually
          </Text>
          <Input
            autoFocus={!isMobile}
            value={extra}
            onChange={e => setExtra(e.target.value)}
            placeholder="e.g. BL-2026-02882 or UCL:BALE:…"
            size="large"
            style={{ height: 48, fontSize: 15, borderRadius: 10 }}
            onPressEnter={() => tryAddByCode(extra)}
          />
        </Space>
      </Modal>
    </div>
  )
}


export default function MoisturePage() {
  // Phase COTTON-MOISTURE-POLISH — inject scoped page CSS once
  useMoistureStyles()

  const qc = useQueryClient()
  // Use the global plant selector — auto-defaults to first plant, persists
  // across navigation via localStorage. Removes the "Pick a plant" empty
  // state that confused operators (see Phase COTTON-QC-001 audit).
  const { plantId, setPlantId, plants } = usePlantId()
  const [activeGrnId, setActiveGrnId] = useState<string | null>(null)

  const { data: grns = [], isLoading, refetch } = useQuery({
    queryKey: ['moisture-grns', plantId],
    queryFn: () => apiClient.get<GRN[]>('/grn', { params: { plant_id: plantId, limit: 100 } })
      .then(r => r.data),
    enabled: !!plantId,
  })

  const buckets = useMemo(() => {
    const today: GRN[] = []
    const pending: GRN[] = []
    const inProg: GRN[] = []
    const completed: GRN[] = []
    const hold: GRN[] = []
    for (const g of grns) {
      const cls = classify(g)
      if (cls === 'HOLD') hold.push(g)
      else if (cls === 'PENDING') pending.push(g)
      else if (cls === 'IN_PROGRESS') inProg.push(g)
      else if (cls === 'COMPLETED') completed.push(g)
      // For today: created today (best-effort by id sorting; backend
      // returns desc-by-date, so first ~30 typically suffice)
      today.push(g)
    }
    return { today: today.slice(0, 20), pending, inProg, completed, hold }
  }, [grns])

  const activeGrn = grns.find(g => g.id === activeGrnId)

  if (activeGrn) {
    return (
      <div className="moisture-page-root"
           style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
        <GrnEntry
          grn={activeGrn}
          onBack={() => setActiveGrnId(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['moisture-grns', plantId] })
          }}
        />
      </div>
    )
  }

  function GrnCardList({ list }: { list: GRN[] }) {
    if (!list.length) return <Empty />
    return (
      <Row gutter={[12, 12]}>
        {list.map(g => {
          const sampled = g.bales.filter(b => b.is_moisture_sample)
          const done = sampled.filter(b => b.moisture_pct != null).length
          const pct = sampled.length ? Math.round((done / sampled.length) * 100) : 0
          const cls = classify(g)
          const accent =
            cls === 'COMPLETED'   ? '#52c41a'
          : cls === 'HOLD'        ? '#ff4d4f'
          : cls === 'IN_PROGRESS' ? '#1677ff'
                                  : '#bfbfbf'
          return (
            <Col xs={24} sm={12} md={8} key={g.id}>
              <Card size="small" hoverable
                     onClick={() => setActiveGrnId(g.id)}
                     className="grn-card"
                     style={{ borderLeft: `4px solid ${accent}` }}>
                <Space direction="vertical" style={{ width: '100%' }} size={6}>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text strong style={{ fontSize: 15, letterSpacing: 0.3 }}>
                      {g.grn_no}
                    </Text>
                    <Tag color={cls === 'COMPLETED' ? 'success'
                                  : cls === 'HOLD' ? 'error'
                                  : cls === 'IN_PROGRESS' ? 'processing'
                                  : 'default'}>{cls}</Tag>
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {g.supplier_name ?? '—'}
                  </Text>
                  <LotTags
                    lots={uniqueLots(g.bales, 'party_lot_no')}
                    color="purple"
                    label="Party Lot"
                  />
                  <LotTags
                    lots={uniqueLots(g.bales, 'mill_lot_no')}
                    color="blue"
                    label="Mill Lot"
                  />
                  <Space>
                    <Badge count={g.physical_bale_count}
                            color="blue" overflowCount={9999} title="Total bales" />
                    <Badge count={sampled.length}
                            color="cyan" overflowCount={9999} title="Required samples" />
                    <Badge count={done}
                            color="green" overflowCount={9999} title="Completed" />
                  </Space>
                  <Progress percent={pct} size="small"
                              status={pct === 100 ? 'success' : 'active'}
                              strokeColor={
                                pct === 100 ? '#52c41a'
                                : { from: '#13c2c2', to: '#52c41a' }
                              } />
                  {g.avg_moisture_pct != null && (
                    <Text>Avg: <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {Number(g.avg_moisture_pct).toFixed(2)}%
                    </Text></Text>
                  )}
                  <Button type="primary" icon={<PlayCircleOutlined />}
                          style={{
                            height: 40, fontSize: 14, fontWeight: 600,
                            borderRadius: 8,
                          }}>
                    {done === 0 ? 'Start'
                     : done === sampled.length ? 'Review' : 'Continue'}
                  </Button>
                </Space>
              </Card>
            </Col>
          )
        })}
      </Row>
    )
  }

  return (
    <div className="moisture-page-root"
         style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>Moisture Entry</Title>
      <Text type="secondary">
        Pick a GRN to enter moisture readings. Sampling floor 25%; QC may
        add extra bales up to 100% per Part A rules.
      </Text>

      <Card style={{ marginTop: 12 }}>
        <Space wrap>
          <Select placeholder="Plant" style={{ width: 240 }}
                  value={plantId || undefined} onChange={setPlantId}
                  options={plants.map(p => ({
                    value: p.id as string,
                    label: (p as { plant_name?: string; name?: string }).plant_name
                            ?? (p as { name?: string }).name ?? '',
                  }))} />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}
                  disabled={!plantId}>Refresh</Button>
        </Space>
      </Card>

      {!plantId ? (
        <Card style={{ marginTop: 12 }}>
          <Empty description="Loading plant — if this persists, check your plant access." />
        </Card>
      ) : isLoading ? <Skeleton active /> : (
        <Tabs style={{ marginTop: 16 }}
              defaultActiveKey="pending"
              items={[
                { key: 'pending', label: `Pending (${buckets.pending.length})`,
                  children: <GrnCardList list={buckets.pending} /> },
                { key: 'inprog', label: `In Progress (${buckets.inProg.length})`,
                  children: <GrnCardList list={buckets.inProg} /> },
                { key: 'completed', label: `Completed (${buckets.completed.length})`,
                  children: <GrnCardList list={buckets.completed} /> },
                { key: 'hold', label: `QC Hold (${buckets.hold.length})`,
                  children: <GrnCardList list={buckets.hold} /> },
                { key: 'all', label: `Recent (${buckets.today.length})`,
                  children: <GrnCardList list={buckets.today} /> },
              ]} />
      )}
    </div>
  )
}
